/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useRef } from 'react';
import {
  Modal, Form, Button, Alert, Spinner, Badge,
} from 'react-bootstrap';
import _ from 'lodash';
import { Typeahead } from 'react-bootstrap-typeahead';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import BookingService from '../../../Services/BookingService';
import setStateFromInput from '../../../Utils/setStateFromInput';
import OperatorService from '../../../Services/OperatorService';
import MapRoutePanel from '../../../Components/MapRoutePanel';
import MultiOperatorAssignment from './MultiOperatorAssignment';
import OperatorQuotePanel from './OperatorQuotePanel';
import { paymentTypeImpliesCash, hasOrphanFlatCash, flatCashPortion } from '../../../Utils/orderCash';
import { assignableOperators, EXPENSE_ROLE, isExpenseAccount } from '../../../Utils/operatorType';
import { isExpenseBooking } from '../../../Utils/bookingType';

function OperatorInputModal({
  originalOrder: initialOrder = {}, show = false, handleClose, fetchOrders,
}) {
  const [originalOrder, setOriginalOrder] = useState(initialOrder);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const defaultOrder = {
    send_notification_to_customer: false,
    operator_price: 0,
    operator_currency: null,
    operator_cash_from_client: null,
    operator_cash_from_client_currency: null,
    operator_comments: !originalOrder.operator_id ? originalOrder.comments : null,
  };

  const [order, setOrder] = useState(() => _.defaults({}, originalOrder, defaultOrder));

  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [decoratedOperators, setDecoratedOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState([]);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [driverPortal, setDriverPortal] = useState('');
  const [isMapCollapsed, setIsMapCollapsed] = useState(false);
  const fetchedRef = useRef(false);

  // Multi-operator mode state - enabled when booking has operators array or user switches to it
  const [useMultiOperatorMode, setUseMultiOperatorMode] = useState(false);

  // Determine if we should show multi-operator UI:
  // 1. User explicitly switched to multi-operator mode
  // 2. Booking already has operators array with entries
  // 3. Booking type is 'experience'
  const hasExistingOperators = order.operators?.length > 0;
  const isExperienceBooking = order.booking_type === 'experience';
  /**
   * An EXPENSE always uses the multi-operator UI, because that is where its
   * amount lives: the cost is `operators[].price`, not the booking's
   * `price` (which is revenue). The legacy single-operator form below writes
   * `operator_id` / `operator_price` and offers suppliers only, so dropping
   * an expense into it would both hide the expense accounts and put the
   * money in the wrong field.
   */
  const isExpense = isExpenseBooking(order);
  const showMultiOperatorUI = useMultiOperatorMode
    || hasExistingOperators
    || isExperienceBooking
    || isExpense;

  // Switch to multi-operator mode
  const switchToMultiOperatorMode = () => {
    // If there's a legacy single operator, migrate it to the operators array
    if (order.operator_id && !order.operators?.length) {
      const migratedOperator = {
        role: 'driver',
        operator_id: order.operator_id,
        driver_id: order.driver_id || null,
        car_id: order.car_id || null,
        price: order.operator_price || null,
        currency: order.operator_currency || 'EUR',
        comments: order.operator_comments || '',
        cash_from_client: order.operator_cash_from_client || null,
        cash_from_client_currency: order.operator_cash_from_client_currency || 'EUR',
      };
      setOrder((prev) => ({
        ...prev,
        operators: [migratedOperator],
      }));
    }
    setUseMultiOperatorMode(true);
  };

  /**
   * An expense is forced into the multi-operator UI, which reads
   * `order.operators` — so a record that still keeps its cost in the LEGACY
   * flat fields would open showing no lines at all and look empty while
   * carrying money.
   *
   * That is not hypothetical. The seven cost records the office faked as
   * transfers before this type existed ("MOL Nyrt. Tankolás", "Autóbérlés",
   * …) all have an empty `operators[]` and their amount in `operator_price`
   * against a top-level `operator_id`. Retyping one as an expense is exactly
   * how they get cleaned up, so lift the flat fields into a row the moment
   * the modal opens — the same migration the "Add Multiple Operators" button
   * performs, just not waiting to be clicked.
   *
   * THE ROLE FOLLOWS THE PAYEE'S TYPE, as a pick in the row would
   * (`MultiOperatorAssignment.chooseOperator`): a cost account is lifted as
   * a Cost (`expense`), anything else as `guide` — never `driver`, because
   * nobody drove anywhere and the driver role is hidden on an expense. Until
   * 2026-11-03 every payee was lifted as `guide`, a cost account included,
   * for want of a cost role. So the lift waits for the operator list
   * (fetched, debounced, on open); without it the payee's type is unknown.
   */
  useEffect(() => {
    if (!isExpense) return;
    if (!order.operator_id) return;
    if (order.operators?.length) return;
    if (!operators?.length) return;

    const payee = operators.find((operator) => operator.Id === order.operator_id);

    setOrder((prev) => ({
      ...prev,
      operators: [{
        role: isExpenseAccount(payee) ? EXPENSE_ROLE : 'guide',
        operator_id: prev.operator_id,
        price: prev.operator_price || null,
        currency: prev.operator_currency || prev.currency || 'EUR',
        comments: prev.operator_comments || '',
      }],
    }));
  }, [isExpense, order.operator_id, order.operators?.length, operators]);

  // Handle multi-operator changes
  const handleOperatorsChange = (newOperators) => {
    setOrder((prev) => ({
      ...prev,
      operators: newOperators,
    }));
  };

  // Fetch fresh booking data when modal opens
  useEffect(() => {
    const fetchBooking = async () => {
      if (show && initialOrder?._id && !fetchedRef.current) {
        fetchedRef.current = true;
        setIsLoadingOrder(true);
        try {
          const freshOrder = await BookingService.getBookingById(initialOrder._id);
          if (freshOrder) {
            setOriginalOrder(freshOrder);
            setOrder(_.defaults(freshOrder, defaultOrder));
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error fetching booking:', err);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };

    fetchBooking();
  }, [show, initialOrder?._id]);

  // Reset fetchedRef when modal closes
  useEffect(() => {
    if (!show) {
      fetchedRef.current = false;
    }
  }, [show]);

  // Check if we have valid coordinates for map
  const hasValidMapCoords = originalOrder.destination_from?.latitude
    && originalOrder.destination_from?.longitude
    && originalOrder.destination_to?.latitude
    && originalOrder.destination_to?.longitude;

  const handleSubmit = async () => {
    setLoading(true);

    try {
      await BookingService.postBooking(order);
      fetchOrders();
      handleClose();
    } catch (err) {
      // The API refuses an expense account in the driver slot (and a
      // customer notification on an expense) with a 400 whose message names
      // the operator and the reason. Surface it rather than swallowing it —
      // and keep a fallback, because `err.response` is absent on a network
      // failure and reading `.data` off it used to throw right here, losing
      // the real error.
      setError(err.response?.data?.message || err.message || 'Failed to save operators');

      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const decorateOperatorOption = (option) => {
    if (!option) return null;

    return {
      id: option.Id,
      label: `${option.name}`,
    };
  };

  const updateOperator = () => {
    if (order.operator_id && operators.length !== 0) {
      const decoratedPartner = decorateOperatorOption(
        operators.find((operator) => operator.Id === order.operator_id),
      );

      if (decoratedPartner) {
        setSelectedOperator([decoratedPartner]);
      }
    }
  };

  const fetchOperators = async () => {
    const response = await OperatorService.fetchOperators();
    setOperators(response?.data.data);
  };

  /**
   * Handle input change
   * @param {object} e
   * @returns void
   */
  const handleChange = (e) => {
    setStateFromInput(e, setOrder, order);
  };

  const debouncedFetchOperators = _.debounce(() => {
    fetchOperators();
  }, 500);

  const addLinkToDriverPortal = () => `Driver Portal: https://portal.pickuphungary.com/driver/${order.operator_id}/${order.booking_reference}`;

  useEffect(() => {
    debouncedFetchOperators();
  }, [originalOrder]);

  useEffect(() => {
    let operatorId;

    if (Array.isArray(selectedOperator) && selectedOperator.length === 1) {
      operatorId = selectedOperator[0].id;

      if (order.partner_id !== operatorId) {
        setOrder((prev) => ({
          ...prev,
          operator_id: operatorId,
        }));
      }
    }
  }, [selectedOperator]);

  useEffect(() => {
    const operatorsTypeAhead = [];

    // ASSIGNMENT: this writes `order.operator_id` — who RUNS the trip. The
    // own-fleet cost buckets are operator records on the same list and must
    // not be offered one. `updateOperator` below still resolves the
    // already-selected operator from the UNFILTERED `operators`, so a
    // booking that legitimately carries a bucket keeps showing its name
    // instead of going blank.
    assignableOperators(operators).forEach((operatorRaw) => {
      operatorsTypeAhead.push(
        decorateOperatorOption(operatorRaw),
      );
    });

    setDecoratedOperators(operatorsTypeAhead);
    updateOperator();
  }, [operators]);

  // Update selected operator only when operator_id changes (not on every order change)
  useEffect(() => {
    updateOperator();
  }, [order.operator_id]);

  useEffect(() => {
    setDriverPortal(addLinkToDriverPortal());
  }, [order.operator_id]);

  // Effective operators array for the quote panel: prefer the multi-operator
  // operators[]; if it's empty but the booking has a legacy single operator
  // (typical for transfer trips, or while the user is mid-edit in the legacy
  // form which sets order.operator_id as soon as the dropdown changes),
  // synthesise one driver row from the flat fields. Shared by both the
  // multi-operator UI and the legacy single-op form so quoting works in both.
  const explicitOperatorRows = (order.operators || [])
    .filter((op) => op.operator_id && (!op.role || op.role === 'driver'));
  const legacyOperatorRow = order.operator_id ? [{
    role: 'driver',
    operator_id: order.operator_id,
    driver_id: order.driver_id,
    car_id: order.car_id,
    price: order.operator_price,
    currency: order.operator_currency,
    // Enrich with the operator's display name so the panel doesn't fall
    // back to showing the raw UUID.
    operator_name: operators.find((o) => o.Id === order.operator_id)?.name,
    // Set when Apply has populated the flat price from a quote in this
    // session — suppresses the "Manual price" badge until next reload.
    pricing_source: order.__operator_price_from_quote ? 'quote' : undefined,
  }] : [];
  const effectiveQuoteOperators = explicitOperatorRows.length > 0
    ? explicitOperatorRows
    : legacyOperatorRow;
  const cameFromLegacy = explicitOperatorRows.length === 0 && legacyOperatorRow.length > 0;
  const quotePanel = originalOrder._id && effectiveQuoteOperators.length > 0 ? (
    <OperatorQuotePanel
      bookingId={originalOrder._id}
      operators={effectiveQuoteOperators}
      onApplied={(operatorId, data) => {
        const bo = data?.booking_operator;
        if (!bo) return;
        const appendix = data?.comments_appendix || '';
        const appendComments = (existing) => {
          if (!appendix) return existing;
          if (!existing) return appendix;
          // Don't duplicate if the same auto-quote breakdown is already
          // there (user clicked Apply twice on the same quote).
          if (existing.includes(appendix)) return existing;
          return `${existing}\n\n${appendix}`;
        };
        setOrder((prev) => {
          const updatedMulti = (prev.operators || []).map((op) => {
            if (op.operator_id !== operatorId) return op;
            return {
              ...op,
              price: bo.price ?? op.price,
              currency: bo.currency ?? op.currency,
              pricing_source: bo.pricing_source ?? 'quote',
              pricing_snapshot_at: bo.pricing_snapshot_at,
              pricing_breakdown: bo.pricing_breakdown,
              comments: appendComments(op.comments),
            };
          });
          if (cameFromLegacy && prev.operator_id === operatorId) {
            return {
              ...prev,
              operators: updatedMulti,
              operator_price: bo.price ?? prev.operator_price,
              operator_currency: bo.currency ?? prev.operator_currency,
              operator_comments: appendComments(prev.operator_comments),
              // Transient UI marker; not sent to the API. Lets the synth row
              // mark itself as quote-sourced so the Manual price badge hides.
              __operator_price_from_quote: true,
            };
          }
          return { ...prev, operators: updatedMulti };
        });
      }}
    />
  ) : null;

  return (
    <Modal show={show} onHide={handleClose} size={showMultiOperatorUI ? 'lg' : 'md'}>
      <Modal.Header closeButton>
        <Modal.Title>
          {/* On an expense this modal is not dispatching anybody — it is
              recording who the money went to and how much. */}
          {isExpense && 'Who is being paid'}
          {!isExpense && (showMultiOperatorUI ? 'Assign Operators' : 'Assign Operator')}
          {isExperienceBooking && (
            <Badge bg="info" className="ms-2">Experience</Badge>
          )}
          {isExpense && (
            <Badge bg="secondary" className="ms-2">Expense</Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingOrder ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Loading booking details...</p>
          </div>
        ) : (
          <>
            {/* Route Map Panel */}
            {hasValidMapCoords && (
            <div className="mb-3">
              <MapRoutePanel
                fromCoords={{
                  lat: originalOrder.destination_from.latitude,
                  lon: originalOrder.destination_from.longitude,
                }}
                toCoords={{
                  lat: originalOrder.destination_to.latitude,
                  lon: originalOrder.destination_to.longitude,
                }}
                fromAddress={originalOrder.destination_from.address}
                toAddress={originalOrder.destination_to.address}
                stops={originalOrder.programme?.stops || []}
                isCollapsed={isMapCollapsed}
                onToggleCollapse={() => setIsMapCollapsed(!isMapCollapsed)}
              />
            </div>
            )}

            <Error
              error={error}
              setError={setError}
            />

            {showMultiOperatorUI ? (
              /* Multi-operator assignment */
              <>
                {isExpense && (
                  <Alert variant="secondary" className="py-2" data-testid="expense-operator-hint">
                    <i className="fa fa-receipt me-2" />
                    Add a line for each party the cost was paid to and put the
                    amount in Price.
                  </Alert>
                )}

                <MultiOperatorAssignment
                  operators={order.operators || []}
                  onChange={handleOperatorsChange}
                  showPricing
                  showCashFromClient={!isExpense && paymentTypeImpliesCash(order)}
                  hideDriverRole={isExperienceBooking || isExpense}
                />

                {/* A quote is somebody's offer to RUN a trip. There is no
                    trip to quote for on a cost record. */}
                {!isExpense && quotePanel}

                {!isExpense && paymentTypeImpliesCash(order) && (
                  <Alert variant="warning" className="mt-3">
                    <Alert.Heading>Payment Info</Alert.Heading>
                    <p className="mb-0">
                      Payment is Cash or Mixed payment.
                      Configure cash collection per operator above.
                    </p>
                  </Alert>
                )}

                <Modal.Footer>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    Save Operators
                  </Button>
                </Modal.Footer>
              </>
            ) : (
              /* Single operator assignment */
              <>
                {/* Button to switch to multi-operator mode */}
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={switchToMultiOperatorMode}
                  >
                    <i className="fa fa-users me-1" />
                    Add Multiple Operators
                  </Button>
                </div>

                <div className="row">
                  <Form.Group className="mb-3 col-12" controlId="operator_id">
                    <Form.Label>Operator</Form.Label>
                    <Typeahead
                      placeholder="Select an operator"
                      id="operator_id"
                      onChange={setSelectedOperator}
                      options={decoratedOperators}
                      selected={selectedOperator}
                    />

                    <FormControlFeedback errors={validationErrors} field="operator_booking_reference" />
                  </Form.Group>

                  {quotePanel && (
                    <div className="col-12 mb-3">{quotePanel}</div>
                  )}

                  <Form.Group className="mb-3 col-12" controlId="operator_booking_reference">
                    <Form.Label>Operator #</Form.Label>
                    <Form.Control
                      value={order.operator_booking_reference}
                      onChange={(e) => handleChange(e)}
                    />

                    <FormControlFeedback errors={validationErrors} field="operator_booking_reference" />
                  </Form.Group>

                  <Form.Group className="mb-3 col-12 col-md-6" controlId="operator_price">
                    <Form.Label>Operator Price</Form.Label>
                    <Form.Control
                      value={order.operator_price}
                      type="number"
                      onChange={(e) => handleChange(e)}
                    />

                    <FormControlFeedback errors={validationErrors} field="operator_price" />
                  </Form.Group>

                  <Form.Group className="mb-3 col-12 col-md-6" controlId="operator_currency">
                    <Form.Label>Currency</Form.Label>
                    <Form.Select
                      value={order.operator_currency}
                      onChange={(e) => handleChange(e)}
                    >
                      <option value="">Select currency</option>
                      <option>EUR</option>
                      <option>HUF</option>
                    </Form.Select>
                    <FormControlFeedback errors={validationErrors} field="operator_currency" />
                  </Form.Group>

                  {paymentTypeImpliesCash(order) && (
                    <Alert variant="warning" onClose={() => setError(false)} dismissible>
                      <Alert.Heading>Payment Info</Alert.Heading>
                      <p>
                        Payment is Cash or Mixed payment.
                        Provide details to operator regarding collection.
                      </p>
                    </Alert>
                  )}

                  {/* Same rule as the multi-operator form: keep a recorded
                      amount visible when the payment type no longer implies
                      cash, so it can be corrected instead of silently riding
                      along on the operator balance. This legacy flat field is
                      the larger population — 639 bookings carry it. */}
                  {(paymentTypeImpliesCash(order) || hasOrphanFlatCash(order)) && (
                    <>
                      <Form.Group className="mb-3 col-12 col-md-6" controlId="operator_cash_from_client">
                        <Form.Label className={hasOrphanFlatCash(order) ? 'text-danger fw-semibold' : undefined}>
                          {hasOrphanFlatCash(order) && (
                            <i
                              className="fa fa-exclamation-triangle me-1"
                              data-testid="orphan-flat-cash-warning"
                            />
                          )}
                          Cash from Client
                        </Form.Label>
                        <Form.Control
                          className={hasOrphanFlatCash(order) ? 'border-danger' : undefined}
                          value={order.operator_cash_from_client}
                          type="number"
                          onChange={(e) => handleChange(e)}
                        />
                        {hasOrphanFlatCash(order) && (
                          <Form.Text className="text-danger">
                            {`Payment type is "${order.payment?.type || '(none)'}" but ${flatCashPortion(order)} cash is recorded. `}
                            Clear it, or set the payment type back to Cash / Mixed Payment.
                          </Form.Text>
                        )}
                        <FormControlFeedback errors={validationErrors} field="operator_cash_from_client" />
                      </Form.Group>

                      <Form.Group className="mb-3 col-12 col-md-6" controlId="operator_cash_from_client_currency">
                        <Form.Label>Cash from Client - Currency</Form.Label>
                        <Form.Select
                          className={hasOrphanFlatCash(order) ? 'border-danger' : undefined}
                          value={order.operator_cash_from_client_currency}
                          onChange={(e) => handleChange(e)}
                        >
                          <option value="">Select currency</option>
                          <option>EUR</option>
                          <option>HUF</option>
                        </Form.Select>
                        <FormControlFeedback errors={validationErrors} field="operator_cash_from_client_currency" />
                      </Form.Group>
                    </>
                  )}

                  <Form.Group className="mb-3 col-12" controlId="operator_comments">
                    <Form.Label className="d-flex">
                      Operator Comments
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      value={order.operator_comments}
                      onChange={(e) => handleChange(e)}
                    />
                  </Form.Group>

                  {/* Booking extras (info-only) — so whoever's editing
                      the operator assignment can see what the customer
                      ordered alongside the trip. Not editable here;
                      services live on the customer side and are managed
                      from OrderInputModal's "Booking Extras" picker. */}
                  {Array.isArray(order.services) && order.services.length > 0 && (
                    <div className="mb-3 col-12">
                      <Alert variant="light" className="border">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fa fa-gift me-2 text-primary" />
                          <strong>Booking Extras</strong>
                          <Badge bg="secondary" className="ms-2">
                            {order.services.length}
                          </Badge>
                          <small className="text-muted ms-auto">information only</small>
                        </div>
                        <ul className="list-unstyled mb-0 small">
                          {order.services.map((svc, idx) => (
                            <li
                              // eslint-disable-next-line react/no-array-index-key
                              key={`${svc.service_id || 'svc'}-${idx}`}
                              className="d-flex justify-content-between border-top py-1"
                            >
                              <span>
                                <strong>{svc.name}</strong>
                                {svc.quantity > 1 && (
                                  <span className="text-muted ms-1">
                                    {`× ${svc.quantity}`}
                                  </span>
                                )}
                                {svc.description && (
                                  <span className="text-muted d-block">
                                    {svc.description}
                                  </span>
                                )}
                              </span>
                              <span className="text-end ms-3">
                                {svc.subtotal ?? 0}
                                {' '}
                                {svc.currency || order.currency || 'EUR'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </Alert>
                    </div>
                  )}

                  {order.partner?.driver_portal_enabled === 1 && order.operator_id && (
                    <p className="alert alert-primary">
                      {driverPortal}
                    </p>
                  )}
                </div>

                <Modal.Footer>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    Assign operator
                  </Button>
                </Modal.Footer>
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default OperatorInputModal;
